import { ApiError, type DeploymentDetail, type HealthState } from '@central/api';
import {
  Card,
  KpiTile,
  StatusChip,
  UptimeStrip,
  color,
  formatAgo,
  formatDateTime,
  toneForHealth,
  formatPercent,
  InfoRow,
  HealthChip,
  formatBytes,
  formatCpuMilli,
} from '@central/ui';
import { Button } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { deploymentQuery } from '../api';
import { ComposeButton } from '../components/ComposeButton';
import { AgentsCard, ApiCard, ServersCard } from '../components/DeploymentHealth';
import { IncidentList } from '../components/IncidentList';
import { Mono } from '../components/Mono';
import { QueryState } from '../components/QueryState';
import { StripEnds, StripLegend } from '../components/StripLegend';
import { databaseDetail, databaseLabel, formatIn, healthLabel } from '../format';

const stateLabel: Record<HealthState, string> = {
  up: 'Running',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
  updating: 'Updating',
};

export function Deployment() {
  const { deploymentId } = useParams({ from: '/app/deployments/$deploymentId' });
  const { data, error } = useQuery(deploymentQuery(deploymentId));
  if (error instanceof ApiError && error.status === 404) return <NotFound />;
  if (!data) return <QueryState error={error} />;
  return <DeploymentBody d={data} />;
}

function Breadcrumb({ name }: { name: string }) {
  return (
    <div style={{ fontSize: 13, color: color.textMuted }}>
      <Link to="/deployments">Deployments</Link> / {name}
    </div>
  );
}

/** The same answer for a missing id and another customer's id. */
function NotFound() {
  return (
    <>
      <Breadcrumb name="Not found" />
      <Card title="Deployment not found">
        <p style={{ margin: 0 }}>
          Check the link, or go back to <Link to="/deployments">your deployments</Link>.
        </p>
      </Card>
    </>
  );
}

/**
 * Renders only the fields the response carries: an absent field is hidden by
 * the environment's visibility. A shared environment never shows service
 * detail, whatever the response holds.
 */
function DeploymentBody({ d }: { d: DeploymentDetail }) {
  const shared = d.kind === 'shared';
  const services = shared ? undefined : d.services;
  const databases = shared ? undefined : d.databases;
  const api = shared ? undefined : d.api;
  const sub: ReactNode[] = [];
  if (shared) sub.push('Hosted by Rethink · shared environment');
  else
    sub.push(d.lastHeartbeatAt ? `Last report ${formatAgo(d.lastHeartbeatAt)}` : 'No report yet');
  if (d.version !== undefined)
    sub.push(
      <>
        runs <Mono>{d.version}</Mono>
      </>,
    );

  return (
    <>
      <Breadcrumb name={d.name} />
      <header
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: '34px' }}>{d.name}</h1>
            <HealthChip state={d.health} labels={healthLabel} />
          </div>
          <div style={{ fontSize: 14, color: color.textMuted }}>
            {sub.map((s, i) => (
              <span key={i}>
                {i > 0 && ' · '}
                {s}
              </span>
            ))}
          </div>
        </div>
        <ComposeButton kind="bug" subject={`Problem with ${d.name}`} environmentId={d.id}>
          Report a problem
        </ComposeButton>
      </header>

      {shared && (
        <div
          role="note"
          style={{
            background: color.rowRule,
            color: color.textBody,
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          Rethink runs this environment for several organizations, so server details and logs aren’t
          shown. Rethink monitors them and posts incidents here.
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
          gap: 12,
        }}
      >
        <KpiTile label="Uptime · 24 h" value={formatPercent(d.uptime.d1)} />
        <KpiTile label="Uptime · 7 d" value={formatPercent(d.uptime.d7)} />
        <KpiTile label="Uptime · 30 d" value={formatPercent(d.uptime.d30)} />
        <KpiTile label="Uptime · 90 d" value={formatPercent(d.uptime.d90)} />
      </div>

      <Card title={`Last ${d.days.length} days`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <UptimeStrip days={d.days} height={32} />
          <StripEnds days={d.days.length} />
          <StripLegend />
        </div>
      </Card>

      {d.modules && (
        <Card title="Modules">
          <StateGrid items={d.modules} />
        </Card>
      )}

      <Columns>
        {d.incidents && (
          <Card
            title={shared ? 'Incidents' : 'Recent incidents'}
            padded={false}
            style={{ flex: '3 1 400px' }}
          >
            <IncidentList incidents={d.incidents} grouped={shared} />
          </Card>
        )}
        {shared ? <SharedVersionCard d={d} /> : <VersionCard d={d} />}
      </Columns>

      {(services || !shared) && (
        <Columns>
          {services && (
            <Card title="Services" style={{ flex: '3 1 400px' }}>
              <StateGrid
                items={services.map((s) => ({
                  name: s.name,
                  state: s.state,
                  detail: `${s.ready}/${s.desired} ready${s.version ? ` · ${s.version}` : ''}`,
                  extra: `CPU ${formatCpuMilli(s.cpuMilli)} · ${formatBytes(s.memBytes)}${
                    s.restarts24h
                      ? ` · ${s.restarts24h} restart${s.restarts24h === 1 ? '' : 's'} in 24 h`
                      : ''
                  }`,
                }))}
              />
            </Card>
          )}
          {!shared && <LicenseCard d={d} />}
        </Columns>
      )}

      {(!!databases?.length || api) && (
        <Columns>
          {!!databases?.length && (
            <Card title="Databases" style={{ flex: '3 1 400px' }}>
              <StateGrid
                items={databases.map((db) => ({
                  name: databaseLabel(db.name),
                  state: db.state,
                  detail: databaseDetail(db) || undefined,
                }))}
              />
            </Card>
          )}
          {api && <ApiCard api={api} />}
        </Columns>
      )}

      {!shared && d.nodes && <ServersCard nodes={d.nodes} />}

      {d.agents && <AgentsCard agents={d.agents} />}
    </>
  );
}

/** "Oct 14" from a UTC time. */
function day(iso: string): string {
  return formatDateTime(iso).split(', ')[0]!;
}

/** "Nov 9, 22:00 – 23:00", or both ends in full when they fall on different days. */
function upgradeWindow(startsAt: string, endsAt: string): string {
  const [endDay, endTime] = formatDateTime(endsAt).split(', ');
  return `${formatDateTime(startsAt)} – ${endDay === day(startsAt) ? endTime : `${endDay}, ${endTime}`}`;
}

/** "1.1" from "1.1.0". */
function releaseLine(version: string): string {
  return version.split('.').slice(0, 2).join('.');
}

function SeatRows({ d }: { d: DeploymentDetail }) {
  return d.seatUsage?.map((s) => (
    <InfoRow key={s.featureKey} label={s.name}>
      <Mono>
        {s.used} / {s.licensed}
      </Mono>
    </InfoRow>
  ));
}

/**
 * A dedicated deployment's version: installed, the newest release and its
 * maintenance cover, the planned upgrade, and the ways to act on a newer one.
 */
function VersionCard({ d }: { d: DeploymentDetail }) {
  if (d.version === undefined) return null;
  const latest = d.latestRelease;
  const behind = latest?.behind === true;
  const line = latest && releaseLine(latest.version);
  return (
    <Card title="Version" style={{ flex: '2 1 280px' }}>
      <InfoRow label="Installed">
        <Mono>
          {d.version}
          {d.installedAt && ` · ${day(d.installedAt)}`}
        </Mono>
      </InfoRow>
      {latest && (
        <>
          <InfoRow label="Latest">
            <Mono>{latest.version}</Mono>
          </InfoRow>
          <InfoRow label="Covered by maintenance">
            {latest.covered ? (
              <StatusChip tone="ok">Yes</StatusChip>
            ) : (
              <StatusChip tone="unk">No</StatusChip>
            )}
          </InfoRow>
        </>
      )}
      {d.nextUpgrade && (
        <InfoRow label="Planned upgrade">
          <span>
            <Mono>{d.nextUpgrade.version}</Mono> ·{' '}
            {upgradeWindow(d.nextUpgrade.startsAt, d.nextUpgrade.endsAt)}
          </span>
        </InfoRow>
      )}
      {behind && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 14 }}>
          {latest.notesUrl ? (
            <Button
              component="a"
              href={latest.notesUrl}
              target="_blank"
              rel="noreferrer"
              variant="default"
              h={40}
            >
              What’s new in {line}
            </Button>
          ) : (
            <Button
              renderRoot={(props) => <Link {...props} to="/releases" />}
              variant="default"
              h={40}
            >
              What’s new in {line}
            </Button>
          )}
          {!d.nextUpgrade && (
            <ComposeButton
              kind="question"
              subject={`Upgrade ${d.name} to ${latest.version}`}
              environmentId={d.id}
            >
              Request upgrade
            </ComposeButton>
          )}
        </div>
      )}
    </Card>
  );
}

/** The license a dedicated deployment runs on: key, seats and the next renewal check. */
function LicenseCard({ d }: { d: DeploymentDetail }) {
  if (d.licenses.length === 0 && !d.seatUsage?.length) return null;
  return (
    <Card title="License in use" style={{ flex: '2 1 280px' }}>
      {d.licenses.map((l) => (
        <InfoRow key={l.id} label="Key">
          <Mono>{l.keyMasked}</Mono>
        </InfoRow>
      ))}
      <SeatRows d={d} />
      {d.nextLicenseCheckAt && (
        <InfoRow label="Next license check">{formatIn(d.nextLicenseCheckAt)}</InfoRow>
      )}
    </Card>
  );
}

/** A shared deployment's version, the upgrade Rethink scheduled, and the caller's seats. */
function SharedVersionCard({ d }: { d: DeploymentDetail }) {
  if (d.version === undefined && !d.seatUsage) return null;
  return (
    <Card title={d.seatUsage ? 'Version & license' : 'Version'} style={{ flex: '2 1 280px' }}>
      {d.version !== undefined && (
        <InfoRow label="Version">
          <Mono>{d.version}</Mono>
        </InfoRow>
      )}
      {d.nextUpgrade && (
        <InfoRow label="Next upgrade">
          {formatDateTime(d.nextUpgrade.startsAt)} · Rethink-scheduled
        </InfoRow>
      )}
      <SeatRows d={d} />
    </Card>
  );
}

function Columns({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
      {children}
    </div>
  );
}

function StateGrid({
  items,
}: {
  items: Array<{ name: string; state: HealthState; detail?: string; extra?: string }>;
}) {
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(190px, 100%), 1fr))',
        gap: 8,
      }}
    >
      {items.map((m) => (
        <li
          key={m.name}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            border: `1px solid ${color.rowRule}`,
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            {m.name}
            {m.detail && <span style={{ fontSize: 12, color: color.textMuted }}>{m.detail}</span>}
            {m.extra && <span style={{ fontSize: 12, color: color.textMuted }}>{m.extra}</span>}
          </span>
          <StatusChip tone={toneForHealth(m.state)}>{stateLabel[m.state]}</StatusChip>
        </li>
      ))}
    </ul>
  );
}
